/**
 * Business logic for the location panel: what the record says now, what the
 * form will send, and what the panel shows. No React.
 *
 * The panel keeps its own copy of the current location rather than re-reading
 * the record after a save: `PUT /works/:id/location` returns the row it
 * wrote, and that row IS the new truth. The record's identity block is updated
 * through `onRecorded` so the two never disagree on screen.
 */

import { countryName } from "@ondo/shared";
import type { LocationKind, SetLocationRequest, WorkState } from "@ondo/shared";

import { LOCATION_LABELS } from "../lib/constants.ts";
import { LOCATION_COPY } from "./location.constants.ts";

/** The location the record holds now — the four fields a reader sees. */
export interface LocationCurrent {
  kind: LocationKind;
  placeText: string | null;
  /** ISO 3166-1 alpha-2, or null for every location recorded before OND-105. */
  countryCode: string | null;
  city: string | null;
}

/**
 * What the form holds. `place` is the raw text; `toRequest` trims it.
 *
 * `countryCode` is "" for "not recorded" rather than null, because it is the
 * value of a `<select>` and an empty option is how a select says nothing.
 */
export interface LocationDraft {
  kind: LocationKind;
  place: string;
  countryCode: string;
  city: string;
}

/** In storage rather than displayed: the safer guess for a work that has just changed hands. */
export const EMPTY_DRAFT: LocationDraft = {
  kind: "in_storage",
  place: "",
  countryCode: "",
  city: "",
};

/**
 * The current location off the record's state, or null.
 *
 * Null covers two things the panel shows the same way: a location withheld
 * by policy (`state` null — the reader may not see the collection) and one
 * never recorded (`locationKind` null). A loan is not a location: a work on
 * loan still has a home, and this panel is about the home.
 */
export function currentOf(state: WorkState | null): LocationCurrent | null {
  if (state === null || state.locationKind === null) return null;
  return {
    kind: state.locationKind,
    placeText: state.placeText,
    countryCode: state.countryCode,
    city: state.city,
  };
}

/** 'Displayed · Sitting room, London', or just 'Displayed'. The identity block's own spelling. */
export function describeCurrent(current: LocationCurrent): string {
  const label = LOCATION_LABELS[current.kind];
  return current.placeText ? `${label} · ${current.placeText}` : label;
}

/**
 * 'Switzerland · Geneva', 'Switzerland', or null when no country is recorded.
 *
 * A SECOND LINE RATHER THAN PART OF THE FIRST, and the country comes first in
 * it. The typed line is what the owner said; this is what the record holds in a
 * form something could one day publish, and an owner about to choose "region
 * only" (OND-47) has to be able to see it. A location recorded before OND-105
 * has no country and gets no line, which is the honest answer rather than a
 * guess at one.
 */
export function describeRegion(current: LocationCurrent): string | null {
  if (current.countryCode === null) return null;
  const country = countryName(current.countryCode);
  return current.city ? `${country} · ${current.city}` : country;
}

/** A draft seeded from what is recorded, so "Update" opens on the current values. */
export function draftFrom(current: LocationCurrent | null): LocationDraft {
  return current
    ? {
        kind: current.kind,
        place: current.placeText ?? "",
        countryCode: current.countryCode ?? "",
        city: current.city ?? "",
      }
    : EMPTY_DRAFT;
}

/** Trimmed; an empty place is no place, not an empty string. */
export function toRequest(draft: LocationDraft): SetLocationRequest {
  const place = draft.place.trim();
  const countryCode = draft.countryCode.trim().toUpperCase();
  const city = draft.city.trim();
  return {
    kind: draft.kind,
    placeText: place === "" ? null : place,
    // Sent as null rather than omitted, because the server writes what it is
    // handed: a work that has moved and been given no country has none, and
    // "leave it as it was" is not a thing this door can mean.
    countryCode: countryCode === "" ? null : countryCode,
    city: city === "" ? null : city,
  };
}

/**
 * The one rule the form can answer itself, and the reason it answers it here:
 * `work_locations_city_needs_a_country` refuses a city with no country, and a
 * 422 for a rule the screen could have explained is a refusal the owner has to
 * guess at. A city with no country is not a region at any grain.
 */
export function describeDraftFault(draft: LocationDraft): string | null {
  if (draft.city.trim() !== "" && draft.countryCode.trim() === "") {
    return LOCATION_COPY.cityNeedsCountry;
  }
  return null;
}

export type LocationView =
  | { kind: "none"; message: string; detail: string | null; prompt: boolean }
  | { kind: "recorded"; line: string; region: string | null };

/**
 * What the panel shows above the form.
 *
 * `prompt` is the question — "Where is it now?" — and it is asked only of a
 * reader who can answer it. A viewer sees "Not recorded." and no question.
 */
export function toLocationView(current: LocationCurrent | null, canAct: boolean): LocationView {
  if (current) {
    return { kind: "recorded", line: describeCurrent(current), region: describeRegion(current) };
  }
  if (canAct) {
    return { kind: "none", message: LOCATION_COPY.ask, detail: LOCATION_COPY.askDetail, prompt: true };
  }
  return { kind: "none", message: LOCATION_COPY.noneReadOnly, detail: null, prompt: false };
}
